import React, { useState, useEffect } from 'react';
import './SurveyPopup.css';
import { analytics } from '../../lib/analytics';
import { emailService } from '../../lib/emailService';

interface SurveyPopupProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  triggeredFromWaitlist?: boolean;
}

const SurveyPopup: React.FC<SurveyPopupProps> = ({ isOpen, onClose, userEmail, triggeredFromWaitlist = false }) => {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState<{[key: string]: string[]}>({});
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState(userEmail || '');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle');
  const [customAnswers, setCustomAnswers] = useState<{[key: string]: string}>({});
  const [isMobile, setIsMobile] = useState(false);
  const [showEmailCollection, setShowEmailCollection] = useState(false);
  const [emailSubmitStatus, setEmailSubmitStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const storedName = sessionStorage.getItem('userName');
    const storedCompany = sessionStorage.getItem('userCompany');
    const storedEmail = sessionStorage.getItem('userEmail');
    
    if (storedName) {
      setName(storedName);
    }
    if (storedCompany) {
      setCompany(storedCompany);
    }
    if (storedEmail) {
      setEmail(storedEmail);
    } else if (userEmail) {
      setEmail(userEmail);
    }
  }, [userEmail]);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen) {
      setCurrentQuestion(1);
      setAnswers({});
      setCustomAnswers({});
      setSubmitStatus('idle');
      setEmailSubmitStatus('idle');
      setEmailError('');
      
      const storedEmail = sessionStorage.getItem('userEmail');
      if (!storedEmail && !userEmail) {
        setShowEmailCollection(true);
        setEmail('');
      } else {
        setShowEmailCollection(false);
      }
    }
  }, [isOpen, userEmail]);

  const questions = [
    {
      id: 'ai_tools',
      text: 'What AI tools are you currently using in your practice?',
      options: [
        { primary: 'Text-based AI', secondary: 'ChatGPT, Claude, Perplexity for research and writing' },
        { primary: 'Image generation AI', secondary: 'Midjourney, DALL-E, Stable Diffusion for concepts' },
        { primary: 'Architecture-specific AI', secondary: 'Veras, Maket, Finch, TestFit, or similar' },
        { primary: 'BIM/CAD AI features', secondary: 'AI tools within Revit, Rhino, SketchUp, AutoCAD' },
        { primary: 'No AI tools yet', secondary: 'Haven\'t started using AI professionally' },
        { primary: 'Other', secondary: '' }
      ]
    },
    {
      id: 'workflow_transformation',
      text: 'Which workflow needs the biggest transformation in your practice?',
      options: [
        { primary: 'Building regulation research and compliance', secondary: 'Faster access to codes and automated compliance checking' },
        { primary: 'Project coordination and team collaboration', secondary: 'Better communication between teams and consultants' },
        { primary: 'Practice knowledge management', secondary: 'Centralised filing, precedents, and institutional memory' },
        { primary: 'Design visualisation and client communication', secondary: 'Rapid rendering, presentations, and stakeholder buy-in' },
        { primary: 'Integrated workspace for all of the above', secondary: 'One platform handling multiple workflow challenges' },
        { primary: 'Something else', secondary: '' }
      ]
    },
    {
      id: 'work_location',
      text: 'Where are your projects primarily located?',
      options: [
        { primary: 'United Kingdom', secondary: 'England, Scotland, Wales, Northern Ireland' },
        { primary: 'United States', secondary: 'IBC, local codes, and state variations' },
        { primary: 'Middle East', secondary: 'UAE, Saudi Arabia, Qatar' },
        { primary: 'Europe', secondary: 'EU standards plus national building codes' },
        { primary: 'Asia Pacific', secondary: 'Australia, New Zealand, Singapore, Hong Kong' },
        { primary: 'Other', secondary: '' }
      ]
    }
  ];

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAnswerToggle = (questionId: string, optionPrimary: string) => {
    const currentAnswers = answers[questionId] || [];
    const isSelected = currentAnswers.includes(optionPrimary);
    
    if (isSelected) {
      const newAnswers = currentAnswers.filter(answer => answer !== optionPrimary);
      setAnswers({
        ...answers,
        [questionId]: newAnswers
      });
    } else {
      const newAnswers = [...currentAnswers, optionPrimary];
      setAnswers({
        ...answers,
        [questionId]: newAnswers
      });
    }
  };

  const handleCustomAnswerChange = (questionId: string, value: string) => {
    setCustomAnswers({
      ...customAnswers,
      [questionId]: value
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    setEmailError('');
    if (newName.trim()) {
      sessionStorage.setItem('userName', newName);
    }
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCompany = e.target.value;
    setCompany(newCompany);
    setEmailError('');
    if (newCompany.trim()) {
      sessionStorage.setItem('userCompany', newCompany);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail.trim()) {
      sessionStorage.setItem('userEmail', newEmail);
    }
  };

  const handleEmailCollectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailError('');
  };

  const handleEmailCollectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address');
      setEmailSubmitStatus('error');
      return;
    }

    if (!name.trim()) {
      setEmailError('Please enter your name');
      setEmailSubmitStatus('error');
      return;
    }

    if (!company.trim()) {
      setEmailError('Please enter your company');
      setEmailSubmitStatus('error');
      return;
    }

    setEmailSubmitStatus('submitting');

    try {
      const result = await analytics.createUser(email, 'survey_direct', {
        name: name.trim(),
        company: company.trim()
      });
      
      if (!result.success && !result.isDuplicate) {
        setEmailError('Failed to save your email. Please try again.');
        setEmailSubmitStatus('error');
        return;
      }

      sessionStorage.setItem('userEmail', email);
      sessionStorage.setItem('userName', name);
      sessionStorage.setItem('userCompany', company);
      setShowEmailCollection(false);
      setEmailSubmitStatus('idle');
    } catch (error) {
      console.error('Error saving email:', error);
      setEmailError('Something went wrong. Please try again.');
      setEmailSubmitStatus('error');
    }
  };

  const handleNext = async () => {
    const currentQ = questions[currentQuestion - 1];
    if (currentQ) {
      try {
        const questionId = currentQ.id;
        const currentAnswers = answers[questionId] || [];
        
        let finalAnswer = [...currentAnswers];
        if ((currentAnswers.includes('Other') || currentAnswers.includes('Something else')) && customAnswers[questionId]) {
          const otherAnswers = currentAnswers.filter(a => a !== 'Other' && a !== 'Something else');
          finalAnswer = [...otherAnswers, customAnswers[questionId]];
        }
        
        await analytics.trackSurveyResponse(
          questionId,
          currentQ.text,
          finalAnswer.join(', '),
          currentQuestion === questions.length
        );
        console.log(`Saved answer for question ${currentQuestion}: ${questionId}`);
      } catch (error) {
        console.error('Error saving survey response:', error);
      }
    }
    
    if (currentQuestion < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // On last question, move to final page
      setCurrentQuestion(questions.length + 1);
      
      // If NOT triggered from waitlist, auto-submit and close
      if (!triggeredFromWaitlist) {
        try {
          console.log('Survey completed - all responses already saved to Supabase');
          
          if (email.trim()) {
            try {
              const emailSent = await emailService.sendSurveyFollowup(email, name);
              if (emailSent) {
                console.log('Survey follow-up email sent successfully');
              } else {
                console.warn('Survey follow-up email failed to send');
              }
            } catch (emailError) {
              console.error('Error sending survey follow-up email:', emailError);
            }
          }
          
          sessionStorage.setItem('surveyCompleted', 'true');
          
          // Close after 2 seconds
          setTimeout(() => {
            onClose();
          }, 2000);
        } catch (error) {
          console.error('Error completing survey:', error);
        }
      }
      // If triggered from waitlist, just show the form (don't auto-submit)
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus !== 'idle') return;
    
    setSubmitStatus('submitting');
    
    try {
      const finalAnswers = { ...answers };
      Object.keys(customAnswers).forEach(questionId => {
        if (answers[questionId]?.includes('Other') || answers[questionId]?.includes('Something else')) {
          const otherAnswers = answers[questionId].filter(a => a !== 'Other' && a !== 'Something else');
          finalAnswers[questionId] = [...otherAnswers, customAnswers[questionId]];
        }
      });
      
      console.log('Survey submitted:', { 
        name, 
        company, 
        email, 
        answers: finalAnswers 
      });
      
      // Save user with name/company if provided
      if (email.trim()) {
        sessionStorage.setItem('userEmail', email);
        // Create or enrich user profile with name/company
        if (name.trim() || company.trim()) {
          try {
            await analytics.createUser(email, 'survey_direct', {
              name: name.trim() || undefined,
              company: company.trim() || undefined
            });
          } catch (createError) {
            console.error('Error creating/enriching user:', createError);
            // Don't block submission if this fails
          }
        }
      }
      if (name.trim()) {
        sessionStorage.setItem('userName', name);
      }
      if (company.trim()) {
        sessionStorage.setItem('userCompany', company);
      }
      
      console.log('Survey completed - all responses already saved to Supabase');
      
      if (email.trim()) {
        try {
          const emailSent = await emailService.sendSurveyFollowup(email, name);
          if (emailSent) {
            console.log('Survey follow-up email sent successfully');
          } else {
            console.warn('Survey follow-up email failed to send');
          }
        } catch (emailError) {
          console.error('Error sending survey follow-up email:', emailError);
        }
      }
      
      sessionStorage.setItem('surveyCompleted', 'true');
      
      setSubmitStatus('submitted');
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting survey:', error);
      setSubmitStatus('idle');
      alert('Something went wrong. Please try again.');
    }
  };

  if (!isOpen) return null;

  const currentQ = questions[currentQuestion - 1];
  const selectedAnswers = answers[currentQ?.id] || [];
  const hasOtherSelected = selectedAnswers.includes('Other') || selectedAnswers.includes('Something else');
  const canProceed = selectedAnswers.length > 0 && (!hasOtherSelected || customAnswers[currentQ?.id]?.trim());

  return (
    <div className="popup-overlay survey-overlay" onClick={handleBackdropClick}>
      <div className="popup-container survey-container">
        <button className="popup-close" onClick={onClose} aria-label="Close popup">
          ×
        </button>
        <button className="popup-close-mobile" onClick={onClose} aria-label="Close popup">
          ×
        </button>
        
        <div className="survey-content">
          {showEmailCollection ? (
            <>
              <div className="survey-email-collection">
                <h2 className="survey-email-title">Get a Free AI Strategy Consultation</h2>
                <p className="survey-email-subtitle">
                  We're offering 500 architects the opportunity to explore how AI could make your workflow smoother, reduce rework and save time. We'll discuss your current processes and identify practical ways AI could support your practice.
                </p>
                <p className="survey-email-cta">
                  Start by completing a short 3-question survey to tell us a bit about your needs.
                </p>
                
                <form className="survey-email-form" onSubmit={handleEmailCollectionSubmit}>
                  <div className="popup-input-dual">
                    <input
                      type="text"
                      className="popup-input half-width"
                      placeholder="Your Name"
                      value={name}
                      onChange={handleNameChange}
                      required
                      disabled={emailSubmitStatus === 'submitting'}
                    />
                    <input
                      type="text"
                      className="popup-input half-width"
                      placeholder="Company"
                      value={company}
                      onChange={handleCompanyChange}
                      required
                      disabled={emailSubmitStatus === 'submitting'}
                    />
                  </div>
                  <div className="popup-input-container">
                    <input
                      type="email"
                      className="popup-input"
                      placeholder="Enter your email"
                      value={email}
                      onChange={handleEmailCollectionChange}
                      required
                      disabled={emailSubmitStatus === 'submitting'}
                      autoFocus
                    />
                    <button 
                      type="submit" 
                      className={`popup-button survey-email-button ${emailSubmitStatus === 'submitting' ? 'popup-button--loading' : ''}`}
                      disabled={emailSubmitStatus === 'submitting'}
                    >
                      {emailSubmitStatus === 'idle' && 'Start Survey'}
                      {emailSubmitStatus === 'error' && 'Try Again'}
                      {emailSubmitStatus === 'submitting' && (
                        <>
                          <span className="loading-spinner"></span>
                          Starting...
                        </>
                      )}
                    </button>
                  </div>
                  {emailError && (
                    <p className="popup-error-message" style={{ color: '#ef4444', marginTop: '8px' }}>
                      {emailError}
                    </p>
                  )}
                  <p className="popup-privacy" style={{ marginTop: '12px' }}>
                    We will never share your information with anyone.
                  </p>
                </form>
              </div>
            </>
          ) : currentQuestion <= questions.length ? (
            <div className="survey-question-wrapper">
              <div className="survey-progress">
                <div className="survey-progress-text">
                  Question {currentQuestion}/3
                </div>
                <div className="survey-progress-bar">
                  <div 
                    className="survey-progress-fill" 
                    style={{ width: `${(currentQuestion / 3) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="survey-question-container">
                <h2 className="survey-question">{currentQ.text}</h2>
                
                <div className="survey-options">
                  {currentQ.options.map((option, index) => {
                    const isSelected = selectedAnswers.includes(option.primary);
                    const showCustomInput = option.primary === 'Other' || option.primary === 'Something else';
                    
                    return (
                      <div key={index} className="survey-option">
                        <label className={`survey-option-label ${isSelected ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            name={currentQ.id}
                            value={option.primary}
                            checked={isSelected}
                            onChange={() => handleAnswerToggle(currentQ.id, option.primary)}
                            className="survey-option-input"
                          />
                          <div className="survey-option-content">
                            <span className="survey-option-primary">{option.primary}</span>
                            {option.secondary && (
                              <span className="survey-option-secondary">{option.secondary}</span>
                            )}
                            {showCustomInput && isSelected && (
                              <input
                                type="text"
                                placeholder="Please specify..."
                                value={customAnswers[currentQ.id] || ''}
                                onChange={(e) => handleCustomAnswerChange(currentQ.id, e.target.value)}
                                className="survey-custom-inline-input"
                                onClick={(e) => e.stopPropagation()}
                                required
                              />
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="survey-actions">
                  <button
                    onClick={handleNext}
                    disabled={!canProceed}
                    className="survey-next-button"
                  >
                    {currentQuestion < questions.length ? 'Next Question' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className={`survey-final-step ${triggeredFromWaitlist ? 'waitlist-triggered' : ''}`}>
              {triggeredFromWaitlist ? (
                <>
                  <h2 className="survey-final-title">Thank you!</h2>
                  <p className="survey-founding-member-title">Limited Founding Member Spots Available</p>
                  <p className="survey-founding-member-subtitle">
                    We're inviting 500 architects to receive a free AI consultation and early access to the platform.
                  </p>
                  
                  <form className="survey-final-form" onSubmit={handleSubmit}>
                    <div className="popup-input-dual">
                      <input
                        type="text"
                        className="popup-input half-width"
                        placeholder="Your Name"
                        value={name}
                        onChange={handleNameChange}
                        required
                        disabled={submitStatus !== 'idle'}
                      />
                      <input
                        type="text"
                        className="popup-input half-width"
                        placeholder="Company"
                        value={company}
                        onChange={handleCompanyChange}
                        required
                        disabled={submitStatus !== 'idle'}
                      />
                    </div>
                    <div className="popup-input-container">
                      <div className="popup-input-row email-row">
                        <input
                          type="email"
                          className="popup-input email-input"
                          placeholder="Enter your email"
                          value={email}
                          onChange={handleEmailChange}
                          required
                          disabled={submitStatus !== 'idle'}
                        />
                        <button 
                          type="submit" 
                          className={`popup-button ${submitStatus !== 'idle' ? 'popup-button--loading' : ''}`}
                          disabled={submitStatus !== 'idle'}
                        >
                          {submitStatus === 'idle' && 'Submit Survey'}
                          {submitStatus === 'submitting' && (
                            <>
                              <span className="loading-spinner"></span>
                              Submitting...
                            </>
                          )}
                          {submitStatus === 'submitted' && (
                            <>
                              <span className="success-checkmark">✓</span>
                              Thank you!
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                  
                  {submitStatus === 'submitted' ? (
                    <p className="popup-success-message">Survey completed! We'll be in touch soon.</p>
                  ) : (
                    <p className="popup-privacy">We will never share your information with anyone.</p>
                  )}
                </>
              ) : (
                <>
                  <h2 className="survey-final-title">Thank you!</h2>
                  <p className="survey-founding-member-title">Your Founding Member application is in!</p>
                  <p className="survey-founding-member-subtitle">
                    We'll be in touch via email soon with next steps and your early access details.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyPopup; 